import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { ProjectService } from '../../api/services/project.services';
import { ProductAliasService } from '../../api/services/product-alias.service';
import { OrderItem } from '../../api/models/order-item';
import { UserService } from '../../api/services/user.service';
import { Advisory, Product } from '../../api/models/product';
import { BomLine } from '../../api/models/bom-line';
import { Observable } from 'rxjs/Observable';
import { forkJoin } from 'rxjs/observable/forkJoin';
import { of } from 'rxjs/observable/of';
import { AuthService } from '../../api/services/auth.service';
import { ToastrService } from 'ngx-toastr';
import { QuoteAdvisoriesResult } from '../../api/models/quote-advisories';
import { CheckoutService } from '../checkout/checkout.service';
import { Worksheet } from '../../api/models/worksheet';
import { RoleRequest, RoleResponse } from '../../api/models/role-assignment-map';
import { OrderLines } from '../../api/models/order-simulation';
import { MarketService } from '../../api/services/market.service';
import { CutCableModalComponent } from '../cut-cable-modal/cut-cable-modal.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { BomTableService } from '../bom-table/bom-table.service';
import { Subject } from 'rxjs';
import { DeleteModalComponent } from '../delete-modal/delete-modal.component';
import { Location } from '@angular/common';
import { OrderDetail } from '../../api/models/order-detail';

@Component({
  selector: 'app-product-advisories',
  templateUrl: './product-advisories.component.html',
  styleUrls: ['./product-advisories.component.scss']
})
export class ProductAdvisoriesComponent implements OnInit {

  worksheetId: string;
  worksheet: Worksheet;
  bomId: string;
  orderItems: OrderItem[];

  orderItemsBackordered: OrderItem[] = [];
  orderItemsNoBackordered: OrderItem[] = [];
  itemsWithAdvisories: OrderItem[] = [];
  backorderedSkus: string;
  nonreturnableSkus: string;
  minimumQtySkus: string;
  hazmatSkus: string;
  subTotalAmount: number;

  step = 1;
  isWarningVisible = true;
  errorMessage: string[] = [];

  loading = true;
  loadingPrice: boolean = false;
  canViewAvailability = false;
  canViewPricing = false;

  skusAndQtys: any;

  roleRequest: RoleRequest;

  orderLines : OrderLines[];
  quoteAdvisories: { bomLineId: string, advisory: Advisory }[] = [];

  quoteAdvisoriesResult: { bomLineId: string, advisoryId: string, value: string }[] = [];
  finalbomLinesAdvisories: QuoteAdvisoriesResult[] = [];

  accountNumber: string;
  warningMessages: string[] = [];
  deleteIndex: number;

  $qtyChangedSubject: Subject<any> = new Subject();

  closeResult = '';
  globalErrorMessages: string[] = [];
  disableAcceptAllButton: boolean = false;
  bomLineArray: BomLine[] = [];

  constructor(
    private projectService: ProjectService,
    private authService: AuthService,
    private userService: UserService,
    private activatedRoute: ActivatedRoute,
    private productService: ProductAliasService,
    private checkoutService: CheckoutService,
    private marketService: MarketService,
    private router: Router,
    private toastr: ToastrService,
    private modalService: NgbModal,
    private bomTableService: BomTableService,
    private location: Location) { }


  ngOnInit() {
    this.activatedRoute
      .params.map(p => p.id)
      .switchMap(id => {
        this.worksheetId = id;
        this.roleRequest = new RoleRequest('AVAILABILITY_VIEW', null);
        this.roleRequest.setRoleAndContextMap('PRICING_VIEW', null);
        return forkJoin(
          this.projectService.getWorksheet(this.worksheetId),
          this.authService.getUserRoles(this.roleRequest),
          this.projectService.getOrderSimulation(this.worksheetId)
        );
      })
      .subscribe((result: [Worksheet, RoleResponse, OrderDetail ]) => {
        if (this.userService.isMarketPermittedForCurrentUser(result[0].project.marketId)) {
          this.worksheet = result[0];
          const roles = result[1];
          this.orderLines = result[2].orderLines;
          this.canViewAvailability = roles.getRoleMap('AVAILABILITY_VIEW');
          this.canViewPricing = roles.getRoleMap('PRICING_VIEW');
          this.marketService.getMarket(this.worksheet.project.marketId).subscribe(market => {
            this.accountNumber = market.accountNumber;
            this.getOrderItems(this.worksheet)
              .subscribe(items => {
                if (items.length > 0) {
                  this.orderItems = items;
                  this.prepareOrderItems(items);
                  this.updateQtyIfNeeded(items);
                  //if any tunable item exists need to set the advisory value if it exists
                  this.setBOMLineAdvisoryValueIfExists();
                  if (this.hazmatSkus || this.nonreturnableSkus ||
                    this.backorderedSkus || this.minimumQtySkus ||
                    this.itemsWithAdvisories.length > 0) {
                    this.loading = false
                  } else {
                    this.router.navigate(['/quote-process', { outlets: { 'quote-process-content': ['checkout', this.worksheetId] } }]);
                    this.loading = false;
                  }
                } else {
                  this.disableAcceptAllButton = true;
                  this.orderItemsNoBackordered = [];
                  this.orderItemsBackordered = [];
                }
                this.loading = false;
              });
          }, error => {
            this.loading = false;
            this.worksheet = null;
            this.worksheetId = null;
            this.errorMessage.push(error.error.message);
          });
        } else {
          this.worksheet = null;
          this.worksheetId = null;
          this.loading = false;
          this.errorMessage.push("You don't have permission to view this page!!!");
        }
      }, error => {
        this.loading = false;
        this.globalErrorMessages.push(error.error.message);
      });

    //Registering on-qty-update event
    this.onQtyChanged();
  }

  setQuoteAdvisories(quoteAdvisoryInput: any) {

    const advisoryIndex = this.quoteAdvisoriesResult.findIndex(adv => adv.bomLineId === quoteAdvisoryInput.bomLineId);
    if (advisoryIndex === -1) {
      this.quoteAdvisoriesResult.push({ bomLineId: quoteAdvisoryInput.bomLineId, advisoryId: quoteAdvisoryInput.advisory.advisoryId, value: quoteAdvisoryInput.advisory.value });
    } else {
      this.quoteAdvisoriesResult[advisoryIndex] =
        ({ bomLineId: quoteAdvisoryInput.bomLineId, advisoryId: quoteAdvisoryInput.advisory.advisoryId, value: quoteAdvisoryInput.advisory.value });
    }
  }

  setAdvisories() {
    //set Tunnable Advisory
    if (this.quoteAdvisoriesResult && this.quoteAdvisoriesResult.length > 0) {
      this.finalbomLinesAdvisories = [];
      this.quoteAdvisoriesResult.forEach(quoteAdv => {
        if (quoteAdv.value != null && quoteAdv.value.trim().length > 0) {
          const newQuoteAdvisory = new QuoteAdvisoriesResult();
          newQuoteAdvisory.bomLineId = quoteAdv.bomLineId;
          newQuoteAdvisory.advisoryValue = quoteAdv.value.trim();
          newQuoteAdvisory.advisoryId = quoteAdv.advisoryId;
          this.finalbomLinesAdvisories.push(newQuoteAdvisory);
        }
      });
    }
  }

  accept() {
    // this.errorMessage = [];
    // let skuThatRequiresAdvisoryAnswers: string[] = [];
    // skuThatRequiresAdvisoryAnswers = this.orderItems
    //   .filter(orderItem => orderItem.data.isTuningItem).map(oi => oi.data.sku);
    // if (skuThatRequiresAdvisoryAnswers.length > 0) {
    //   this.setAdvisories();
    //   if (skuThatRequiresAdvisoryAnswers.length !== this.finalbomLinesAdvisories.length) {
    //     let skus = this.orderItems
    //       .filter(orderItem => orderItem.data.isTuningItem &&
    //         orderItem.data.advisories[0].value === undefined || orderItem.data.advisories[0].value == "")
    //       .map(oi => oi.data.sku);
    //     this.errorMessage.push("Please provide the advisory answer for the following SKUS: " + skus + "");
    //     return;
    //   }
    //   this.checkoutService.setBomLinesAdvisoriesResults(this.finalbomLinesAdvisories);
    // }
    this.router.navigate(['/quote-process', { outlets: { 'quote-process-content': ['checkout', this.worksheet.id] } }]);
  }

  private gatherNonreturnableSkus() {
    //NonCancellableNonreturnable skus
    let nonreturnableSkusArray = [];
    this.orderItems.forEach((orderItem) => {
      if (orderItem.isNonReturnable()) {
        nonreturnableSkusArray.push(orderItem.data.sku);
      }
    });
    if (nonreturnableSkusArray.length > 0) {
      const uniqueNcnrSkus = new Set(nonreturnableSkusArray);
      this.nonreturnableSkus =  this.formatSKUS(uniqueNcnrSkus);
    }
  }

  private gatherBackorderedSkus() {
    let backOrderedSkusArray = [];
    this.orderItemsBackordered.forEach((orderItem) => {
      backOrderedSkusArray.push(orderItem.data.sku);
    });
    if (backOrderedSkusArray.length > 0) {
      const uniqueBackOrderedSkus = new Set(backOrderedSkusArray);
      this.backorderedSkus = this.formatSKUS(uniqueBackOrderedSkus);
    }
  }

  private gatherMinimumQtySkus() {
    let minimumQtySkusArray = [];
    this.orderItems.forEach((orderItem) => {
      if (orderItem.notFitMinimum()) {
        minimumQtySkusArray.push(orderItem.data.sku);
      }
    });
    if (minimumQtySkusArray.length > 0) {
      const uniqueMinimumQtySkus = new Set(minimumQtySkusArray);
      this.minimumQtySkus = this.formatSKUS(uniqueMinimumQtySkus);
    }
  }

  private gatherHazmatSkus() {
    let hazmatSkusArray = [];
    this.orderItems.forEach((orderItem) => {
      if (orderItem.isHazmat()) {
        hazmatSkusArray.push(orderItem.data.sku);
      }
    });
    if (hazmatSkusArray.length > 0) {
      const uniqueHazmatSkus = new Set(hazmatSkusArray);
      this.hazmatSkus = this.formatSKUS(uniqueHazmatSkus);
    }
  }

  private calculateSubTotal() {
    let subTotal = 0;
    this.orderLines.forEach(ol => {
      subTotal = subTotal + ol.quantity * ol.price;
    });
    this.subTotalAmount = subTotal;
  }

  private getOrderItems(worksheet: Worksheet): Observable<OrderItem[]> {
    this.bomId = worksheet.bomId;
    return this.projectService.getBomLines(this.bomId)
      .switchMap((bomLines: BomLine[]) => {
        this.skusAndQtys = bomLines.map(i => ({ sku: i.partNumber, quantity: i.quantity }));
        let skusArray = [];
        this.skusAndQtys.forEach(skuAndQty => {
          skusArray.push(skuAndQty.sku);
        });
        if (skusArray.length == 0) {
          return of([]);
        }
        this.bomLineArray = bomLines;
        const skus = this.skusAndQtys.map(b => b.sku);
        return this.productService.productDetailsV2(skus, this.accountNumber)
          .switchMap((details: Product[]) => {
            const orderItems = bomLines.reduce((prev: OrderItem[], curr: BomLine) => {
              const product = details.find(p => p.sku === curr.partNumber);
              const availability = {
                qty: this.orderLines.find(line => line.bomLineId === curr.id).quantity,
                qtyAvailable: this.orderLines.find(line => line.bomLineId === curr.id).quantityAvailable
              };
              return prev.concat([new OrderItem(curr, product, availability)]);
            }, []);

            return of(orderItems);
          });

      });
  }

  private prepareOrderItems(items: OrderItem[]) {
    this.orderItems = items;
    this.itemsWithAdvisories = this.filterOrderItemsWithAdvisories(items);
    this.gatherNonreturnableSkus();
    this.gatherMinimumQtySkus();
    this.gatherHazmatSkus();
    if (this.canViewAvailability) {
      this.splitLists(this.orderItems);
      this.gatherBackorderedSkus();
    } else {
      this.orderItemsNoBackordered = this.orderItems;
    }
    if (this.canViewPricing) {
      this.calculateSubTotal();
    }
    if (this.hazmatSkus) {
      this.warningMessages.push("The cart contains hazmat items " + this.hazmatSkus + ".");
    }
    if (this.nonreturnableSkus) {
      this.warningMessages.push("The cart contains non-returnable items  " + this.nonreturnableSkus + ".");
    }
    if (this.backorderedSkus) {
      this.warningMessages.push("The cart contains products that are on back order " + this.backorderedSkus + ".");
    }
    if (this.minimumQtySkus) {
      this.warningMessages.push("Quantity has been adjusted to meet the minimum requirements " + this.minimumQtySkus + ".");
    }
  }

  private filterOrderItemsWithAdvisories(orderItems: OrderItem[]): OrderItem[] {
    return orderItems.filter(oi =>{
      if(oi.data){
        oi.data.advisories
      }});
  }

  private filterOrderItemsBackordered(orderItems: OrderItem[]): OrderItem[] {
    return orderItems.filter(oi => oi.isBackordered());
  }

  private filterOrderItemsNoBackordered(orderItems: OrderItem[]): OrderItem[] {
    return orderItems.filter(oi => !oi.isBackordered());
  }

  private splitLists(orderItems: OrderItem[]) {
    this.orderItemsBackordered = this.filterOrderItemsBackordered(orderItems);
    this.orderItemsBackordered.sort(this.compareObject);
    this.orderItemsNoBackordered = this.filterOrderItemsNoBackordered(orderItems);
    this.orderItemsNoBackordered.sort(this.compareObject);
  }

  compareObject(orderItem1: OrderItem, orderItem2: OrderItem) {
    if (orderItem1.data.advisorySortOrder < orderItem2.data.advisorySortOrder) {
      return -1;
    }
    if (orderItem1.data.advisorySortOrder > orderItem2.data.advisorySortOrder) {
      return 1;
    }
    return 0;
  }

  private onUpdateSuccess(message: string) {
    this.toastr.success(message, 'Succcess!', {
      timeOut: 2000,
      positionClass: 'toast-bottom-right'
    });
  }

  private onUpdateError(message: string) {
    this.toastr.error(message, 'Error!', {
      timeOut: 3000,
      positionClass: 'toast-bottom-right'
    });
  }

  updateQtyIfNeeded(orderItems: OrderItem[]) {
    const itemsToUpdate = orderItems.filter(item => item.notFitMinimum());
    itemsToUpdate.forEach(item => {
      this.updateQuantity(item.line, item.data.minimumQuantity, item.data.isCableItem);
      const messgage = "The SKU " + item.line.partNumber + " is adjusted to Min quantity " 
      + item.data.minimumQuantity;
      const messagePresentIndex = this.warningMessages.indexOf(messgage);
      if(messagePresentIndex < 0){
        this.warningMessages.push(messgage);
      }
    });
  }


  updateQuantity(bomLine: BomLine, quantity: number, isCableItem: boolean) {
    const updatedBomLine = bomLine;
    if (bomLine.quantity.toString() !== quantity.toString()) {
      bomLine.quantity = quantity;
      updatedBomLine.accountNumber = this.accountNumber;
      updatedBomLine.isCableSkuQtyAdjusted = isCableItem;
      this.loadingPrice = true;
      this.projectService.updateBomLine(this.bomId, updatedBomLine).subscribe(() => {
        this.onUpdateSuccess('The item quantity was updated.');
        this.orderItems.find(item => item.line.id === bomLine.id).line.quantity = quantity;
        this.skusAndQtys = this.skusAndQtys.filter(elm => elm.sku !== updatedBomLine.partNumber);
        this.skusAndQtys.push({ sku: updatedBomLine.partNumber, quantity: quantity });
        this.projectService.getOrderSimulation(this.worksheetId).subscribe(orderSimulationDetails => {
          this.orderLines = orderSimulationDetails.orderLines;
          this.calculateSubTotal();
          this.loadingPrice = false;
        });
        this.gatherMinimumQtySkus();
        this.updateQtyIfNeeded(this.orderItems);
      },
        () => this.onUpdateError('There was a problem updating the item.')
      );
    }
  }

  onQtyChanged() {
    this.$qtyChangedSubject
      .debounceTime(1000)
      .distinctUntilChanged()
      .subscribe((data: [BomLine, HTMLInputElement]) => {
        const updatedBomLine = data[0];
        const updatedQty = data[1];

        let qtyObtained: number = Number(updatedQty.value);
        if (qtyObtained <= 0) {
          this.disableAcceptAllButton = true;
          this.onUpdateError('Please enter Quantity > 0');
          return false;
        } else {
          this.disableAcceptAllButton = false;
        }

        if(updatedBomLine.isConsignmentItem){
          this.onUpdateError('Consigned items can be modified in worksheet');
          return false;
        }
        this.loadingPrice = true;
        updatedBomLine.quantity = Number(updatedQty.value);
        updatedBomLine.accountNumber = this.accountNumber;
        this.projectService.updateBomLine(this.bomId, updatedBomLine).subscribe(() => {
          this.onUpdateSuccess('The item quantity was updated.');
          this.orderItems.find(item => item.line.id === updatedBomLine.id).line.quantity = Number(updatedQty.value);
         // this.skusAndQtys = this.skusAndQtys.filter(elm => elm.sku !== (updatedBomLine.isConsignmentItem ? updatedBomLine.partNumber : updatedBomLine.partNumber));
          this.skusAndQtys.push({ sku: (updatedBomLine.isConsignmentItem ? updatedBomLine.partNumber : updatedBomLine.partNumber), quantity: updatedQty.value });
          this.simulateOrderDetails();
          this.gatherMinimumQtySkus();
          this.updateQtyIfNeeded(this.orderItems);
        },
          () => this.onUpdateError('There was a problem updating the item.')
        );
      });
  }

  private simulateOrderDetails() {
    this.projectService.getOrderSimulation(this.worksheetId).subscribe(orderSimulationDetails => {
      this.orderLines = orderSimulationDetails.orderLines;
      this.calculateSubTotal();
      this.loadingPrice = false;
    });
  }

  onDeleteMaterial(bomLineId: string, data: Product, index: number) {
    const modalRef = this.modalService.open(DeleteModalComponent, {
      backdrop: 'static',
      keyboard: false
    });
    modalRef.componentInstance.bodyMsg = `Are you sure you want to remove the material <strong>${data.sku} (${data.manufacturerPartNumber})</strong> from the worksheet?`;
    modalRef.componentInstance.primaryButtonText = "Yes, Remove";
    modalRef.componentInstance.fn = () => {
      modalRef.close();
      this.onConfirmDeleteMaterial(bomLineId, index)
    };
  }

  onConfirmDeleteMaterial(bomLineId: string, index: number) {
    this.deleteIndex = index;
    this.projectService.deleteBomLine(bomLineId).subscribe(
      () => {
        this.onUpdateSuccess('The item was deleted.');
        this.warningMessages = [];
        this.loadingPrice = true;
        this.projectService.getOrderSimulation(this.worksheetId).subscribe(orderSimulationDetails => {
          this.orderLines = orderSimulationDetails.orderLines;   
          this.loadingPrice = false;
          this.getOrderItems(this.worksheet).subscribe(items => {
            if (items.length > 0) {
              this.prepareOrderItems(items);
              this.setBOMLineAdvisoryValueIfExists();
              this.disableAcceptAllButton = false;
            } else {
              this.disableAcceptAllButton = true;
              this.orderItemsNoBackordered = [];
              this.orderItemsBackordered = [];
            }
            this.deleteIndex = undefined;
          });
        });
      },
      () => {
        this.onUpdateError('There was a problem deleting the item.');
        this.deleteIndex = undefined;
      }
    );
  }

  cutCable(bomLineItem: any) {
    const modalRef = this.modalService.open(CutCableModalComponent, {
      backdrop: 'static',
      keyboard: false
    });
    modalRef.componentInstance.bomId = this.bomId;
    modalRef.componentInstance.bomLineId = bomLineItem.line.id;
    modalRef.result
      .then(newQuantity => {
        bomLineItem.line.quantity = newQuantity;
        this.bomTableService.addWaitingQuoteItems([bomLineItem.line.id]);
        //Refreshing the cable cut table after edit
        let orderItem = this.orderItems.find(oi => oi.line.id === bomLineItem.line.id);
        this.projectService.GetCableCuts(this.bomId, bomLineItem.line.id).subscribe(cableCutList => {
          orderItem.line.cableCuts = cableCutList;
          this.loadingPrice = true;
          this.simulateOrderDetails();
        });
      })
      .catch(console.error);
  }

  setDefaultProductPic(event) {
    event.target.src = 'assets/images/no-image.jpg';
  }

  closeWarningMessage() {
    this.warningMessages = [];
  }

  closeErrorMessage() {
    this.errorMessage = [];
  }

  private setBOMLineAdvisoryValueIfExists() {
    if (this.itemsWithAdvisories.length > 0 &&
      this.itemsWithAdvisories.some(item => item.data.isTuningItem)) {
      this.quoteAdvisoriesResult = [];
      let bomLineIds = this.bomLineArray.map(bomline => bomline.id).join();
      this.projectService.getBomLineAdvisoryList(bomLineIds).subscribe(bomeLinesAdv => {
        if (bomeLinesAdv.length > 0) {
          let bomLineAdvisoriesObtained: QuoteAdvisoriesResult[] = [];
          bomLineAdvisoriesObtained = bomeLinesAdv as QuoteAdvisoriesResult[];
          bomLineAdvisoriesObtained.forEach(bomLineAdvisry => {
            let orderRow = this.orderItems.filter(orderItem => orderItem.line.id === bomLineAdvisry.bomLineId);
            if (orderRow.length && orderRow[0].data.advisories.length) {
              orderRow[0].data.advisories[0].value = bomLineAdvisry.advisoryValue;
            }
            this.quoteAdvisoriesResult.push({
              bomLineId: bomLineAdvisry.bomLineId,
              advisoryId: bomLineAdvisry.advisoryId, value: bomLineAdvisry.advisoryValue
            });
          });
        }
      });
    }
  }

  /**
   * Format SKU Set values
   * Note: If we add a space after coma, contents will be adjusted automatically within div
   * @param setOfSkus 
   * @returns 
   */
   private formatSKUS(setOfSkus: Set<any>) {
    return "(SKUS " + Array.from(setOfSkus).join(", ") + ")";
  }

  goBack(): void {
    this.location.back();
  }
}



// WEBPACK FOOTER //
// /codebuild/output/src3408204168/src/bitbucket.org/tesscotds/webclient/src/app/product-advisories/product-advisories.component.ts